// navigation-completion Stage E — generated proposal queue with accept / reject
// / accept-with-manual-target.
//
// Accept/reject operate on the SERVER proposal id (the server re-derives and
// re-checks the proposal; the client never supplies the target to trust). A
// manual target is explicitly review/manual provenance and needs the separate
// `manual` operation. Every proposal shows its precise evidence, resolved
// target, candidate count and current disposition — a zero-proposal list is
// labelled honestly, never presented as "no errors".

import type { RemoteNavigationPreviewCallback } from './useRemoteNavigationPreview';
import { useEffect, useState, type ReactElement } from 'react';
import type {
  EditTarget,
  NavigationProposal,
  OperationDecisions,
  ProposalOperation,
} from '@/api/documentNavigation';
import {
  OPERATION_LABEL,
  authReasonText,
  describeDisposition,
  describeEvidence,
  describeTarget,
  isProposalTargetUnresolved,
  navStyles,
  targetFromDraft,
  toneColor,
  EMPTY_LOCAL_TARGET,
  type TargetDraft,
} from './navShared';
import { TargetFields } from './navTargetFields';
import type { PdfPreviewFocus } from './PdfCanvasViewer';

function decisionKeyFor(op: ProposalOperation): keyof OperationDecisions {
  // The policy operation an accept requires (the server enforces this too).
  return op === 'createBookmark' ? 'createBookmark' : op === 'createHyperlink' ? 'createHyperlink' : 'linkExistingToc';
}

export interface NavigationProposalsProps {
  readonly proposals: readonly NavigationProposal[];
  readonly canMutate: boolean;
  readonly mutationBlockedReason: string | null;
  readonly decisions: OperationDecisions;
  readonly pageCount: number | null;
  readonly busy: boolean;
  readonly onDispose: (
    proposalId: string,
    decision: 'accept' | 'reject',
    manualTarget: EditTarget | undefined,
    actionKey: string,
  ) => void;
  readonly onJumpToPage: (page1: number) => void;
  /** Rich preview request (evidence source rect / resolved target page). When
   *  present the "View evidence"/"View target" affordances route through it. */
  readonly onPreviewFocus?: (focus: PdfPreviewFocus) => void;
  /** Preview a cross-document manual target's exact bytes in the workspace. */
  readonly onPreview?: RemoteNavigationPreviewCallback;
  /** The source document id — enables the cross-document manual-target picker. */
  readonly documentId: string;
  /** Reports whether a manual-target form is open with unsaved input, so the
   *  parent can register it as workspace draft state. */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

export function NavigationProposals({
  proposals,
  canMutate,
  mutationBlockedReason,
  decisions,
  pageCount,
  busy,
  onDispose,
  onJumpToPage,
  onPreviewFocus,
  onPreview,
  documentId,
  onDirtyChange,
}: NavigationProposalsProps): ReactElement {
  const [manualFor, setManualFor] = useState<string | null>(null);
  const [manualDraft, setManualDraft] = useState<TargetDraft>(EMPTY_LOCAL_TARGET);
  const [localError, setLocalError] = useState<string | null>(null);
  useEffect(() => { onDirtyChange?.(manualFor !== null); }, [manualFor, onDirtyChange]);

  const manualAllowed = canMutate && !!decisions.manual?.allowed;
  const manualReason = !canMutate
    ? mutationBlockedReason
    : decisions.manual && !decisions.manual.allowed
      ? authReasonText(decisions.manual.reason)
      : null;

  return (
    <section style={navStyles.editorPanel} data-testid="nav-proposals" aria-label="Generated proposals">
      {proposals.length === 0 ? (
        <div style={navStyles.muted} data-testid="nav-proposals-empty">
          No navigation proposals have been generated yet. Use “Generate missing navigation” to
          analyse the source. An empty list is not a pass — it means nothing has been proposed.
        </div>
      ) : (
        <div data-testid="nav-proposal-list">
          {proposals.map((p) => {
            const disp = describeDisposition(p.disposition);
            const acceptDec = decisions[decisionKeyFor(p.operation)];
            const unresolved = isProposalTargetUnresolved(p);
            const canAccept = canMutate && !!acceptDec?.allowed;
            const acceptReason = !canMutate
              ? mutationBlockedReason
              : acceptDec && !acceptDec.allowed
                ? authReasonText(acceptDec.reason)
                : null;
            // An unresolved target cannot be accepted as-is or jumped to; the
            // reviewer must pick a destination via "Accept with a different
            // target". The reason is surfaced on the disabled Accept button.
            const acceptBlockedReason =
              acceptReason ??
              (unresolved
                ? 'the proposed destination did not resolve — use “Accept with a different target” to choose one'
                : null);
            const targetPage =
              !unresolved && p.target.kind === 'localPage' ? p.target.pageIndex + 1 : null;
            return (
              <div key={p.id} style={navStyles.row} data-testid={`nav-proposal-${p.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={navStyles.label}>
                    {OPERATION_LABEL[p.operation]}: {p.title || '(untitled)'}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: toneColor(disp.tone) }} data-testid={`nav-proposal-state-${p.id}`}>
                    {disp.text}
                  </span>
                </div>
                <div style={{ ...navStyles.muted, marginTop: 2 }}>Evidence: {describeEvidence(p)}</div>
                <div
                  style={{ ...navStyles.muted, marginTop: 2 }}
                  data-testid={`nav-proposal-target-${p.id}`}
                >
                  {unresolved ? (
                    <span style={{ color: 'var(--warning, #b45309)' }} data-testid={`nav-proposal-unresolved-${p.id}`}>
                      Target unresolved — choose a destination. {p.candidateCount} candidate
                      {p.candidateCount === 1 ? '' : 's'} found.
                    </span>
                  ) : (
                    <>
                      Target: {describeTarget(p.target)} · {p.candidateCount} candidate
                      {p.candidateCount === 1 ? '' : 's'}
                    </>
                  )}
                </div>
                {disp.detail && (
                  <div style={{ ...navStyles.muted, marginTop: 2 }} data-testid={`nav-proposal-detail-${p.id}`}>
                    {disp.detail}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  {p.evidence.page > 0 && onPreviewFocus && (
                    <button
                      type="button"
                      style={navStyles.secondaryBtn}
                      data-testid={`nav-proposal-evidence-${p.id}`}
                      onClick={() => onPreviewFocus({ page: p.evidence.page, highlight: p.evidence.rect, tone: 'source', label: `Evidence: ${p.evidence.kind}` })}
                    >
                      View evidence
                    </button>
                  )}
                  {targetPage !== null && (
                    <button
                      type="button"
                      style={navStyles.secondaryBtn}
                      data-testid={`nav-proposal-jump-${p.id}`}
                      onClick={() =>
                        onPreviewFocus
                          ? onPreviewFocus({ page: targetPage, tone: 'destination', label: `Proposed target · page ${targetPage}` })
                          : onJumpToPage(targetPage)
                      }
                    >
                      {onPreviewFocus ? 'View target' : 'Jump to target page'}
                    </button>
                  )}
                  <PropBtn
                    label="Accept"
                    disabled={!canAccept || busy || unresolved}
                    reason={acceptBlockedReason}
                    testid={`nav-proposal-accept-${p.id}`}
                    onClick={() => onDispose(p.id, 'accept', undefined, `accept-${p.id}`)}
                  />
                  <PropBtn
                    label="Accept with a different target"
                    disabled={!manualAllowed || busy}
                    reason={manualReason}
                    testid={`nav-proposal-manual-btn-${p.id}`}
                    onClick={() => {
                      setManualDraft(EMPTY_LOCAL_TARGET);
                      setManualFor(p.id);
                      setLocalError(null);
                    }}
                  />
                  <PropBtn
                    label="Reject"
                    disabled={!canMutate || busy}
                    reason={!canMutate ? mutationBlockedReason : null}
                    testid={`nav-proposal-reject-${p.id}`}
                    onClick={() => onDispose(p.id, 'reject', undefined, `reject-${p.id}`)}
                  />
                </div>
                {manualFor === p.id && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }} data-testid={`nav-proposal-manual-form-${p.id}`}>
                    <TargetFields value={manualDraft} onChange={setManualDraft} pageCount={pageCount} idPrefix={`nav-proposal-manual-${p.id}`} documentId={documentId} {...(onPreview ? { onPreview } : {})} {...(onPreviewFocus ? { onPreviewFocus } : {})} />
                    <button
                      type="button"
                      style={busy || !manualAllowed || (manualDraft.kind === 'remote' && !manualDraft.selection) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
                      data-testid={`nav-proposal-manual-save-${p.id}`}
                      disabled={busy || !manualAllowed || (manualDraft.kind === 'remote' && !manualDraft.selection)}
                      onClick={() => {
                        const r = targetFromDraft(manualDraft, pageCount);
                        if (!r.ok) {
                          setLocalError(r.error);
                          return;
                        }
                        setLocalError(null);
                        setManualFor(null);
                        onDispose(p.id, 'accept', r.target, `accept-manual-${p.id}`);
                      }}
                    >
                      Accept this target
                    </button>
                    <button type="button" style={navStyles.secondaryBtn} onClick={() => setManualFor(null)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {localError && (
        <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-proposal-error">
          {localError}
        </div>
      )}
    </section>
  );
}

interface PropBtnProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly reason: string | null;
  readonly testid: string;
  readonly onClick: () => void;
}

function PropBtn({ label, disabled, reason, testid, onClick }: PropBtnProps): ReactElement {
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      aria-disabled={disabled}
      title={disabled && reason ? reason : undefined}
      style={disabled ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
